import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { AutoTooltip, Badge, Pagination, SelectPageSize } from "nlib/ui";
import { getTextsData } from "selectors/texts";
import { useSelector } from "react-redux";
import ActionCellContent from "nlib/common/ActionCellContent";
import Constants from "const/Constants";
import ContactSubTypes from "const/ContactSubTypes";
import Countries from "utils/Countries";
import React, { useCallback, useMemo, useState } from "react";
import Table, { TableCell, TableHead, TableRow } from "nlib/ui/Table";
import useEnvVars from "hooks/useEnvVars";

const VENDOR_BADGE_STYLE = {
  [ContactSubTypes.VENDOR]: "primary",
  [ContactSubTypes.CUSTOMER]: "positive"
};

const FlagIcon = React.memo(({ countryCode }) => {
  const { countries } = useSelector(getTextsData);

  const [loadingError, setLoadingError] = useState(false);

  const flagUrl = useMemo(() => Countries.getFlagIconUrl(countryCode), [countryCode]);

  const countryName = countries[countryCode];

  const handleImageError = useCallback(() => {
    setLoadingError(true);
  }, []);

  return (
    <div className={Css.flagIcon}>
      {loadingError
        ? <div title={countryName}><span>{countryCode}</span></div>
        : <img title={countryName} src={flagUrl} onError={handleImageError} />}
    </div>
  );
});

const ContactsTable = ({ tableData, filteredData, fetchingData, simplifyLayout }) => {
  const [envVars, setEnvVars] = useEnvVars();

  const { uiTexts, countries } = useSelector(getTextsData);

  const { page = 1, pageSize = Constants.TABLE_PAGE_SIZE } = envVars;

  const handleTableRowClick = useCallback((event) => {
    if (!event.target.closest("a") && event.currentTarget.dataset.id) {
      setEnvVars({ editItem: event.currentTarget.dataset.id });
    }
  }, [setEnvVars]);

  const handlePageChange = useCallback((value) => {
    setEnvVars({ page: value });
  }, [setEnvVars]);

  const handlePageSizeChange = useCallback((value) => {
    setEnvVars({ pageSize: value });
  }, [setEnvVars]);

  return (
    <>
      <Table
        className={Css.table}
        sortBy={envVars.sortBy}
        sortOrder={envVars.sortOrder}
        onSortChange={setEnvVars}>
        <TableRow>
          <TableHead className={Css.nameCell} accessor="name">{uiTexts.name}</TableHead>
          <TableHead className={Css.subTypeCell} accessor="subType">{uiTexts.type}</TableHead>
          <TableHead className={Css.vatIdCell} accessor="vatId">{uiTexts.vatId}</TableHead>
          <TableHead className={Css.phoneCell} accessor="phone">{uiTexts.phone}</TableHead>
          <TableHead className={Css.email} accessor="email" show={!simplifyLayout}>{uiTexts.email}</TableHead>
          <TableHead className={Css.addressCell} accessor="address" show={!simplifyLayout}>{uiTexts.address}</TableHead>
          <TableHead className={Css.countryCell} accessor="countryCode" show={!simplifyLayout}>
            {uiTexts.country}
          </TableHead>
          <TableHead className={Css.actionsCell}>{uiTexts.actions}</TableHead>
        </TableRow>
        {tableData.map((item) => {
          const {
            id,
            name,
            vatId,
            phone,
            email,
            street,
            city,
            state,
            zipCode,
            countryCode,
            subType
          } = item;

          const address = [street, city, state, zipCode].filter((value) => value);

          const addressString = address.join(" ") + (countryCode ? `, ${countries[countryCode] || countryCode}` : "");

          return (
            <TableRow key={id} data-id={id} className={Css.tableRow} onClick={handleTableRowClick}>
              <TableCell className={Css.nameCell}>
                <AutoTooltip>
                  <Icons.User />
                  <span>{name || Constants.EMPTY_PLACEHOLDER}</span>
                </AutoTooltip>
              </TableCell>
              <TableCell className={Css.subTypeCell}>
                {subType && uiTexts[subType]
                  ? <Badge title={uiTexts[subType]} theme={VENDOR_BADGE_STYLE[subType]}>{uiTexts[subType]}</Badge>
                  : Constants.EMPTY_PLACEHOLDER}
              </TableCell>
              <TableCell className={Css.vatIdCell}>
                <AutoTooltip active={!!vatId}>{vatId || Constants.EMPTY_PLACEHOLDER}</AutoTooltip>
              </TableCell>
              <TableCell className={Css.phoneCell}>
                <AutoTooltip active={!!phone}>
                  {phone
                    ? <a href={`tel:${phone}`}>
                      <Icons.Phone />
                      <span>{phone}</span>
                    </a>
                    : Constants.EMPTY_PLACEHOLDER}
                </AutoTooltip>
              </TableCell>
              <TableCell show={!simplifyLayout} className={Css.email}>
                <AutoTooltip active={!!email}>
                  {email
                    ? <a href={`mailto:${email}`}>
                      <Icons.At />
                      <span>{email}</span>
                    </a>
                    : Constants.EMPTY_PLACEHOLDER}
                </AutoTooltip>
              </TableCell>
              <TableCell show={!simplifyLayout} className={Css.addressCell}>
                <AutoTooltip active={!!address.length}>
                  {address.length
                    ? (
                      countryCode
                        ? (
                          <a
                            target="_blank"
                            href={`${Constants.GOOGLE_MAPS_SEARCH_URL}/${addressString.replace(" ", "+")}`}>
                            <Icons.Globe />
                            <span>{addressString}</span>
                          </a>
                        )
                        : (
                          <>
                            <Icons.Globe />
                            <span>{addressString}</span>
                          </>
                        )
                    )
                    : Constants.EMPTY_PLACEHOLDER}
                </AutoTooltip>
              </TableCell>
              <TableCell show={!simplifyLayout} className={Css.countryCell}>
                {countryCode ? <FlagIcon countryCode={countryCode} /> : Constants.EMPTY_PLACEHOLDER}
              </TableCell>
              <TableCell className={Css.actionsCell}>
                <ActionCellContent menuItems={[{ action: "edit" }]} />
              </TableCell>
            </TableRow>
          );
        })}
      </Table>
      <div className={Css.footer}>
        {(filteredData.length > +pageSize) && (
          <Pagination
            className={Css.pagination}
            count={filteredData.length}
            page={+page}
            pageSize={+pageSize}
            disabled={fetchingData}
            onChange={handlePageChange} />
        )}
        {!!filteredData.length && (
          <SelectPageSize
            pageSize={Number(pageSize)}
            disabled={fetchingData}
            onChange={handlePageSizeChange} />
        )}
      </div>
    </>
  );
};

export default React.memo(ContactsTable);
